import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  SvgIcon,
  TextField,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';

import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import CustomButton from '../../../../../components/General/CustomButton';
import CustomTable from '../../../../../components/General/CustomTable';

import {
  PhenologicalStateWithCycle,
  PhenologicalStateWithId,
} from '../../../../../requests/api/apiTypes';
import { CULT_CYCLE_LIST_UI, FIELDS_EQ, RouteParams } from '../../types';

import CustomModal from '../../../../../components/General/CustomModal';
import { UserRole, UserType } from '../../../../../constants/user_types';
import apiClient from '../../../../../requests/api/apiClient';
import { ReduxState } from '../../../../../types';
import { PhenologicalStateChecker } from '../../../../../utils/varieties';
import { FetchError } from '../../../../types';
import useStyles from '../styles';
import { CultivationCycle } from '../types';
import UpsertPhenologicalStateModalContent from './UpsertPhenologicalStateModalContent';

// TO DO: Al momento de crearse estados fenológicos deben crearse con un uuid
function PhenologicalStatesList({
  cultivationCycles,
  phenologicalStatesWCycle,
  setRefreshPhenologicalStates,
}: {
  cultivationCycles: CultivationCycle[];
  phenologicalStatesWCycle: PhenologicalStateWithCycle;
  setRefreshPhenologicalStates: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [upsertModal, setUpsertModal] = useState({ id: '', open: false });
  const [selectedCycle, setSelectedCycle] = useState<CultivationCycle>(cultivationCycles[0] || {
    id: '', name: '', startDate: '', endDate: '',
  });
  const { user } = useSelector((state: ReduxState) => state.account);
  const isAdmin = user?.role === UserRole.ADMIN && user?.type === UserType.CDTEC;
  const isAgro = user?.role === UserRole.AGRO && user?.type === UserType.CDTEC;
  const isOperator = user?.role === UserRole.OPERATOR && user?.type === UserType.CDTEC;

  const canCreate = isAdmin || isAgro;
  const canEdit = isAdmin || isAgro || isOperator;
  const canDelete = isAdmin || isAgro;

  // get current sector id
  const { id } = useParams<RouteParams>();

  const sectorId = id?.split('?')[0];

  const handleRemovePhenologicalState = async (
    phenologicalStateNameId: string, cycleId: string,
  ) => {
    try {
      const response = await apiClient.phenologicalState
        .phenologicalStateDelete({ phenologicalStateNameId, cycle: cycleId, sector: sectorId || '' });

      enqueueSnackbar(response.data.message, { variant: 'success' });

      // Refresh data from API instead of manual state update
      setRefreshPhenologicalStates((prev) => !prev);
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  useEffect(() => {
    const updatedSelectedCycle = cultivationCycles.find((cycle) => cycle.id === selectedCycle.id);

    if (updatedSelectedCycle) {
      setSelectedCycle(updatedSelectedCycle);
    } else if (cultivationCycles.length > 0) {
      setSelectedCycle(cultivationCycles[0]);
    } else {
      setSelectedCycle({
        id: '', name: '', startDate: '', endDate: '',
      });
    }
  }, [cultivationCycles]);

  const convertPhenologicalStatesToTableData = useCallback(() => {
    const newStates = phenologicalStatesWCycle[selectedCycle.id];
    if (!newStates) return { count: 0, values: [] };
    const sortedPhenologicalStates = PhenologicalStateChecker
      .sortByStartDate(newStates);
    const updatedLocalData = {
      count: sortedPhenologicalStates.length,
      values: sortedPhenologicalStates.map((state) => ({
        id: state.id,
        name: state.name,
        startingDate: dayjs(state.startDate).utc().format('YYYY-MM-DD'),
        endingDate: dayjs(state.endDate).utc().format('YYYY-MM-DD'),
        kcMin: state.kcMin || state.kcMin === 0 ? state.kcMin : '-',
        kcMax: state.kcMax || state.kcMax === 0 ? state.kcMax : '-',
        actions: (
          <>
            {canEdit && (
              <IconButton size="large" onClick={() => setUpsertModal({
                id: state.id,
                open: true,
              })}>
                <SvgIcon fontSize="small">
                  <EditIcon />
                </SvgIcon>
              </IconButton>
            )}
            {canDelete && (
              <IconButton size="large" onClick={() => handleRemovePhenologicalState(
                state.phenologicalStateNameId || '',
                selectedCycle.id,
              )}>
                <SvgIcon fontSize="small">
                  <DeleteIcon />
                </SvgIcon>
              </IconButton>
            )}
          </>
        ),
      })),
    };
    return updatedLocalData;
  }, [phenologicalStatesWCycle, selectedCycle]);

  const handleUpsertPhenologicalState = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    phenologicalState: PhenologicalStateWithId & { phenologicalStateNameId?: string },
  ) => {
    const requiredKeys: (keyof PhenologicalStateWithId)[] = [
      'startDate', 'endDate', 'cycle', 'sector',
    ];
    const hasMissingFields = requiredKeys.some((key) => {
      const value = phenologicalState[key];
      if (!value) {
        enqueueSnackbar(`El campo ${FIELDS_EQ[key as keyof typeof FIELDS_EQ]} debe ser completado`, { variant: 'error' });
        return true;
      }
      return false;
    });
    if (!phenologicalState.phenologicalStateNameId) {
      enqueueSnackbar(`El campo ${FIELDS_EQ.name} debe ser completado`, { variant: 'error' });
      return;
    }

    if (hasMissingFields) return;

    if (!selectedCycle) {
      enqueueSnackbar('No se pudo encontrar el ciclo de cultivo seleccionado', { variant: 'error' });
      return;
    }

    try {
      if (upsertModal.id) {
        const response = await apiClient.phenologicalState.phenologicalStateUpdate(
          upsertModal.id,
          undefined,
          phenologicalState.startDate,
          phenologicalState.endDate,
          phenologicalState.kcMin,
          phenologicalState.kcMax,
        );
        enqueueSnackbar(response.data.message, { variant: 'success' });
      } else {
        const response = await apiClient.phenologicalState.phenologicalStateCreate({
          phenologicalStateNameId: phenologicalState.phenologicalStateNameId,
          cycle: selectedCycle.id,
          sector: sectorId || '',
          startDate: phenologicalState.startDate,
          endDate: phenologicalState.endDate,
          kcMin: phenologicalState.kcMin,
          kcMax: phenologicalState.kcMax,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any);
        enqueueSnackbar(response.data.message, { variant: 'success' });
      }
      setUpsertModal({ id: '', open: false });
      setRefreshPhenologicalStates((prev) => !prev);
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const getStateToEdit = () => {
    if (selectedCycle && upsertModal.id) {
      const state = phenologicalStatesWCycle[selectedCycle.id].find(
        (phenState) => phenState.id === upsertModal.id,
      );
      return state || null;
    }
    return null;
  };

  return (
    <>
      <Box mt={1}>
        <Card>
          <CardContent>
            <Grid
              container
              justifyContent="space-between"
              spacing={3}
              alignItems="center"
            >
              <Grid item>
                <TextField
                  label="Cultivo"
                  select
                  fullWidth
                  value={cultivationCycles.length > 0 ? selectedCycle.name : 'No hay cultivos'}
                  variant="standard"
                  onChange={(e) => setSelectedCycle(
                    cultivationCycles.find(
                      (cycle) => cycle.name === e.target.value,
                    ) || cultivationCycles[0],
                  )
                  }
                  disabled={cultivationCycles.length === 0}
                >
                  {cultivationCycles.length > 0 ? (
                    cultivationCycles.map((cycle) => (
                      <MenuItem key={cycle.id} value={cycle.name}>
                        {cycle.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="No hay cultivos">
                      No hay cultivos
                    </MenuItem>
                  )}
                </TextField>
              </Grid>
              {canCreate && (
                <Grid item>
                  <CustomButton
                    buttonText='Agregar Estado fenológico'
                    color='blue'
                    size='small'
                    onClick={() => setUpsertModal({ id: '', open: true })}
                    disabled={cultivationCycles.length === 0}
                  />
                </Grid>
              )}
            </Grid>

            <Divider className={classes.divider} />

            <CustomTable
              rows={convertPhenologicalStatesToTableData()}
              headers={CULT_CYCLE_LIST_UI(canEdit || canDelete)}
            />
          </CardContent>
        </Card>
      </Box>
      <CustomModal
        open={upsertModal.open}
        handleClose={() => setUpsertModal({ id: '', open: false })}
        data={{ id: upsertModal.id }}
      >
        <UpsertPhenologicalStateModalContent
          handleCloseModal={() => setUpsertModal({ id: '', open: false })}
          handleUpsertFunction={handleUpsertPhenologicalState}
          id={upsertModal.id}
          initialData={getStateToEdit() || undefined}
          cultivationCycle={selectedCycle}
          phenologicalStates={phenologicalStatesWCycle[selectedCycle.id] || []}
        />
      </CustomModal>
    </>
  );
}

export default PhenologicalStatesList;
